// hooks, navigation
import React, { useState, useEffect } from 'react';
import {useNavigate} from "react-router-dom";

// Material UI
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";

// Material UI icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// My modules
import { useAuth } from 'auth/Auth';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PaymentTemplateModal from "./PaymentTemplateModal";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import { useMemo } from "react";
import StatusBadge from "components/StatusBadge";
import MonoText from "components/MonoText";

// IF need to add the action row back in one column.
//
// import IconButton from "@mui/material/IconButton";
// import SettingsIcon from '@mui/icons-material/Settings';


const PaymentTemplates = () => {

    const { axiosInstance } = useAuth();
    const navigate = useNavigate();

    // Initializing states

    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

    // Trigger GET request on first render

    useEffect(() => {
        fetchTemplates();
    }, []);

    // GET request to fetch payment templates data.

    const fetchTemplates = async () => {
        setIsLoading(true);
        setIsCreating(false);
        try {
            const response = await axiosInstance.get('/payment-templates');
            setTemplates(response.data);
        } catch (error) {
            console.error('Error fetching templates:', error);
            setSnackbar({ open: true, message: 'Error loading templates', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Save operation when the template is saved.
    // POST request for creating new entry.
    // PUT request for updating entry.

    const handleSaveTemplate = async (formData) => {
        try {
            const editing = !!selectedTemplate;
            setIsEditing(editing);
            setIsCreating(true);
            let payload = {
                template_name: formData.template_name,
                payment_type: formData.payment_type,
                status: formData.status,
            };
            if (formData.payment_type === "deposit_final_balance") {
                payload = {
                    ...payload,
                    deposit_percentage: formData.deposit_percentage,
                    due_days_before_departure: formData.due_days_before_departure,
                    buffer_days: formData.buffer_days,
                };
            } else if (formData.payment_type === "deposit_installments") {
                payload = {
                    ...payload,
                    deposit_percentage: formData.deposit_percentage,
                    number_of_installments: formData.number_of_installments,
                    installments: formData.installments,
                    buffer_days: formData.buffer_days,
                };
            }

            if (selectedTemplate) {
                await axiosInstance.put(`/payment-templates/${selectedTemplate.id}`, payload);
                setSnackbar({ open: true, message: 'Payment Template updated successfully', severity: 'success' });
            } else {
                await axiosInstance.post('/payment-templates', payload);
                setSnackbar({ open: true, message: 'Payment Template created successfully', severity: 'success' });
            }
            // Send GET request to get updated list of payment templates.
            fetchTemplates();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving template:', error);
            setSnackbar({ open: true, message: 'Error saving template', severity: 'error' });
        } finally {
            setIsCreating(false);
            setIsEditing(false);
        }
    }

    // Update delete state when delete button is clicked.

    const handleDeleteClick = (id) => {
        setDeleteConfirm({ open: true, id });
    };

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/payment-templates/${deleteConfirm.id}`);
            setSnackbar({ open: true, message: 'Payment Template deleted successfully', severity: 'success' });
            fetchTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            setSnackbar({ open: true, message: 'Error deleting template', severity: 'error' });
            throw error;
        }
    }

    const handleCloseDeleteModal = () => setDeleteConfirm({ open: false, id: null });

    // Modal close handler.

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTemplate(null);
    }

    // Open detailed view page on row click

    const handleRowClick = (rowData) => {
        navigate(`/payment-templates/${rowData.id}`, { state: { name: rowData.name } });
    }

    const tableData = useMemo(() => ({
        columns: [
            {
                Header: "Name",
                accessor: "template_name",
                width: "20%",
                Cell: ({ row }) => (
                    <MDTypography component="span" onClick={() => handleRowClick(row.original)} variant="caption" color="info" fontWeight="medium" sx={{ cursor: 'pointer' }}>
                        {row.original.template_name}
                    </MDTypography>
                ),
            },
            {
                Header: "Payment Type",
                accessor: "payment_type",
                width: "25%",
                Cell: ({ value }) => {
                    if (value === "full_payment") return "Full Payment";
                    if (value === "deposit_final_balance") return "Deposit + Final Balance";
                    if (value === "deposit_installments") return "Deposit + Installments";
                    return "Unknown";
                },
            },
            {
                Header: "Deposit %",
                accessor: "deposit_percentage",
                width: "12%",
                align: "right",
                Cell: ({ value }) => (
                    <MonoText align="right">{value ? `${value}%` : "-"}</MonoText>
                ),
            },
            {
                Header: "Status",
                accessor: "status",
                width: "14%",
                Cell: ({ value }) => <StatusBadge status={value} />,
            },
            {
                Header: "Actions",
                accessor: "actions",
                width: "12%",
                align: "center",
                Cell: ({ row }) => (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => { setSelectedTemplate(row.original); setIsModalOpen(true); }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteClick(row.original.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ),
            },
        ],
        rows: templates,
    }), [templates]);

    // Component

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                    <Grid container spacing={6}>
                        <Grid item xs={12}>
                            <Card>
                                <MDBox
                                    mx={2}
                                    mt={-3}
                                    py={2}
                                    px={2}
                                    variant="gradient"
                                    bgColor="info"
                                    borderRadius="lg"
                                    coloredShadow="info"
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <MDBox>
                                        <MDTypography variant="h6" color="white">Payment Templates</MDTypography>
                                        <MDTypography variant="caption" color="white" opacity={0.86}>Configure global billing modes.</MDTypography>
                                    </MDBox>
                                    <MDBox display="flex" alignItems="center" gap={2}>
                                        {isCreating && (
                                            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={16} sx={{ color: "white" }} />
                                                <MDTypography variant="caption" color="white">
                                                    {isEditing ? 'Updating' : 'Creating'}...
                                                </MDTypography>
                                            </MDBox>
                                        )}
                                        <MDButton variant="gradient" color="dark" onClick={() => setIsModalOpen(true)}>
                                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                            &nbsp;Create Template
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                                <MDBox pt={3}>
                                    <DataTable
                                        table={tableData}
                                        isSorted
                                        noEndBorder
                                        canSearch
                                        exportCSV
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        showTotalEntries
                                        pagination={{ variant: "gradient", color: "info" }}
                                        objectType="payment_template"
                                        onFilterClear={fetchTemplates}
                                    />
                                </MDBox>
                            </Card>
                        </Grid>
                    </Grid>
                </MDBox>

                {/* Payment Template Modal : Create / Update */}

                <PaymentTemplateModal
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    onSave={handleSaveTemplate}
                    template={selectedTemplate}
                />

                {/* Snackbar : Info, Error messages */}

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        severity={snackbar.severity}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                <ConfirmDeleteModal
                    open={deleteConfirm.open}
                    onClose={handleCloseDeleteModal}
                    onConfirm={handleDelete}
                    title="Delete Payment Template"
                    message="Are you sure you want to delete this payment template? This action cannot be undone."
                />

        </DashboardLayout>
    )

}

export default PaymentTemplates;